/**
 * Multi-Payer Editor for one enrollment.
 *
 * Reads/writes /api/v1/enrollments/{id}/payers. Validates that the sum
 * across all rows is exactly 100%. Falls back gracefully when no rows
 * are configured (legacy single-payer mode).
 */
import React, { useEffect, useState } from 'react';
import { Plus, Trash2, AlertTriangle, Loader2, CreditCard } from 'lucide-react';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatDeNumber } from '@/lib/utils';

export default function MultiPayerEditor({ enrollmentId, allGuardians = [] }) {
  const [payers, setPayers] = useState([]);
  const [fallback, setFallback] = useState(false);
  const [draft, setDraft] = useState(null); // editable copy
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/enrollments/${enrollmentId}/payers`);
      setPayers(r.data.items || []);
      setFallback(r.data.fallback_used);
    } catch {
      setPayers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (enrollmentId) load(); }, [enrollmentId]);

  const startEdit = () => {
    setDraft(payers.map((p) => ({
      _key: `${p.guardian_id}-${Math.random().toString(36).slice(2, 8)}`,
      guardian_id: p.guardian_id,
      share_pct: p.share_pct,
    })));
    setEditing(true);
  };

  const cancel = () => { setDraft(null); setEditing(false); };

  const totalDraft = draft ? Math.round(draft.reduce((a, p) => a + (parseFloat(p.share_pct) || 0), 0) * 100) / 100 : 0;
  const isValidSum = Math.abs(totalDraft - 100) < 0.01;

  const save = async () => {
    if (!draft) return;
    if (!isValidSum) {
      toast.error(`Summe der Anteile muss 100% sein (aktuell ${totalDraft}%)`);
      return;
    }
    if (draft.some((d) => !d.guardian_id)) {
      toast.error('Bitte für jede Zeile eine:n Zahlungspflichtige:n auswählen');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/enrollments/${enrollmentId}/payers`, draft.map((d) => ({
        guardian_id: d.guardian_id,
        share_pct: parseFloat(d.share_pct),
      })));
      toast.success('Anteile gespeichert');
      setEditing(false);
      setDraft(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const distributeEvenly = () => {
    if (!draft || draft.length === 0) return;
    const each = Math.floor(10000 / draft.length) / 100; // 2 decimals
    const remainder = Math.round((100 - each * draft.length) * 100) / 100;
    setDraft(draft.map((d, i) => ({
      ...d,
      share_pct: i === 0 ? each + remainder : each,
    })));
  };

  if (loading) {
    return <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="multi-payer-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-brand-red" />
          <h4 className="font-semibold text-slate-800 text-sm">Zahlungs-Anteile</h4>
          {fallback && (
            <Badge>Legacy: 1 Person 100 %</Badge>
          )}
        </div>
        {!editing ? (
          <Button size="sm" variant="outline" onClick={startEdit} data-testid="payer-edit-start">
            Anteile anpassen
          </Button>
        ) : (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={distributeEvenly} className="text-xs" data-testid="payer-distribute">
              Gleichverteilung
            </Button>
            <Button size="sm" variant="outline" onClick={cancel} disabled={saving}>Abbrechen</Button>
            <Button size="sm" onClick={save} disabled={saving || !isValidSum} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="payer-save">
              {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Speichern
            </Button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="border border-slate-200 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="text-left py-1.5 px-2">Zahlungspflichtige:r</th>
                <th className="text-left py-1.5 px-2">E-Mail</th>
                <th className="text-right py-1.5 px-2">Anteil/Monat</th>
              </tr>
            </thead>
            <tbody>
              {payers.length === 0 ? (
                <tr><td colSpan={3} className="py-3 text-center text-slate-400 italic">Keine Zahlungspflichtigen konfiguriert</td></tr>
              ) : payers.map((p) => (
                <tr key={p.id || p.guardian_id} className="border-t border-slate-100">
                  <td className="py-1.5 px-2 font-medium">{p.guardian_name || '—'}</td>
                  <td className="py-1.5 px-2 text-slate-500">{p.guardian_email || '—'}</td>
                  <td className="py-1.5 px-2 text-right font-mono font-semibold text-brand-red">
                    {formatDeNumber(p.share_pct)} %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-brand-red/20 rounded p-3 bg-brand-red/5 space-y-2">
          {draft.map((row, idx) => (
            <div key={row._key || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`payer-draft-row-${idx}`}>
              <div className="col-span-7">
                <select
                  value={row.guardian_id || ''}
                  onChange={(e) => {
                    const next = [...draft]; next[idx] = { ...next[idx], guardian_id: e.target.value }; setDraft(next);
                  }}
                  className="h-8 w-full border border-slate-200 rounded px-2 text-xs"
                  data-testid={`payer-draft-guardian-${idx}`}
                >
                  <option value="">— Zahlungspflichtige:n wählen —</option>
                  {allGuardians.map((g) => (
                    <option key={g.id} value={g.id}>
                      {`${g.first_name || ''} ${g.last_name || ''}`.trim() || g.email || g.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-3 flex items-center gap-1">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={row.share_pct}
                  onChange={(e) => {
                    const next = [...draft]; next[idx] = { ...next[idx], share_pct: e.target.value }; setDraft(next);
                  }}
                  className="h-8 font-mono text-xs"
                  data-testid={`payer-draft-pct-${idx}`}
                />
                <span className="text-xs text-slate-500">%</span>
              </div>
              <div className="col-span-2 text-right">
                <button
                  type="button"
                  onClick={() => setDraft(draft.filter((_, i) => i !== idx))}
                  className="text-rose-600 hover:text-rose-700 text-xs"
                  data-testid={`payer-draft-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5 inline" />
                </button>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDraft([...draft, { _key: Math.random().toString(36).slice(2, 10), guardian_id: '', share_pct: 0 }])}
              className="text-xs"
              data-testid="payer-draft-add"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Zahler hinzufügen
            </Button>
            <div className={`text-xs font-semibold ${isValidSum ? 'text-emerald-600' : 'text-rose-600'}`}>
              {!isValidSum && <AlertTriangle className="w-3 h-3 inline mr-1" />}
              Summe: <span className="font-mono">{formatDeNumber(totalDraft)} %</span>
              {!isValidSum && <span className="ml-1">(muss 100 % sein)</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Badge({ children }) {
  return <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-semibold">{children}</span>;
}
